import { GaebPositionType, GetTimeFromTimestamp, LV, LVPosition } from '@dave/types';
import { SelectSearchOption } from '../../dave-utils-module/select-search/components/select-search/select-search.component';
import { AccountsReceivableLedgerTypeEnum } from './accounts-receivable-ledger.entity';

export enum LVPositionState {
    Created = "Created",
    Tendered = "Tendered",
    Awarded = "Awarded"
}
export const LVPositionStateLabels: Record<LVPositionState, string> = {
    [LVPositionState.Created]: 'Erstellt',
    [LVPositionState.Tendered]: 'Ausgeschrieben',
    [LVPositionState.Awarded]: 'Vergeben',
};
export const LVPositionStateAvaLvEntryilableNextStatus: Record<LVPositionState, LVPositionState[]> = {
    [LVPositionState.Created]: [LVPositionState.Created, LVPositionState.Tendered, LVPositionState.Awarded],
    [LVPositionState.Tendered]: [LVPositionState.Tendered, LVPositionState.Awarded],
    [LVPositionState.Awarded]: [LVPositionState.Awarded ],
};


export enum LVPositionType {
    Trade = "Trade",
    Title = "Title",
    Position = "Position",
    Text = "Text"
}

export const LVPositionTypeLabels: Record<LVPositionType, string> = {
    [LVPositionType.Trade]: '',
    [LVPositionType.Title]: 'Gruppe',
    [LVPositionType.Position]: 'Position',
    [LVPositionType.Text]: 'Text',
};
export const LVPositionTypeEditablePositionNumberByUser: Record<LVPositionType, boolean> = {
    [LVPositionType.Trade]: false,
    [LVPositionType.Title]: true,
    [LVPositionType.Position]: false,
    [LVPositionType.Text]: false,
};
export const LVPositionTypeCanHaveChildren: Record<LVPositionType, boolean> = {
    [LVPositionType.Trade]: false,
    [LVPositionType.Title]: true,
    [LVPositionType.Position]: false,
    [LVPositionType.Text]: false,
};

export const LVPositionTypeCanHavePrices: Record<LVPositionType, boolean> = {
    [LVPositionType.Trade]: false,
    [LVPositionType.Title]: false,
    [LVPositionType.Position]: true,
    [LVPositionType.Text]: false,
};

export const GabePositionTypeToLVPositionType: Record<GaebPositionType, LVPositionType> = {
    [GaebPositionType.Text]: LVPositionType.Text,
    [GaebPositionType.Group]: LVPositionType.Title,
    [GaebPositionType.Normal]: LVPositionType.Position,
}

export class AvaLvEntryEntity implements SelectSearchOption {
    public static readonly DEFAULT_ORDER_GAP = 10000;
    constructor(
        public Id: number = null,
        public UUID: string = null,
        public ParentId: number | string = null,
        public OrderNumber: number = null,
        public ArticleNumber: string = null,
        public PositionNumber: string = null,
        public Quantity: number = null,

        public QuantityTypeId: number = null,
        public EstimatedCostLabor: number = null,
        public EstimatedCostOther: number = null,
        public PlannedCostLabor: number = null,
        public PlannedCostOther: number = null,
        public PlannedCostSurchargesLabor: number = null,
        public PlannedCostSurchargesOther: number = null,
        public PlannedSalesPriceLabor: number = null,
        public PlannedSalesPriceOther: number = null,
        public ActualCost: number = null,
        public TenderAssignmentId: number = null,
        public PositionState: LVPositionState = LVPositionState.Created,
        public PositionType: LVPositionType = null,
        public Description: string = null,
        public Title: string = null,

        public UpdatedAt: Date = null,
        /**
         * Kostenart
         */
        public BVCustomNameId: number = null,
    ) {}
    // public static EntityName = 'AVALVENTRY';
    public static EntityPropertyNames: Omit<
        Record<keyof AvaLvEntryEntity, string>,
        | 'Id'
        | 'UUID'
        | 'ParentId'
        | 'OrderNumber'
        | 'Quantity'
        | 'QuantityTypeId'
        | 'EstimatedCostLabor'
        | 'EstimatedCostOther'
        | 'PlannedCostLabor'
        | 'PlannedCostOther'
        | 'PlannedCostSurchargesLabor'
        | 'PlannedCostSurchargesOther'
        | 'PlannedSalesPriceLabor'
        | 'PlannedSalesPriceOther'
        | 'ActualCost'
        | 'TenderAssignmentId'
        | 'PositionState'
        | 'PositionType'
        | 'DisplayName'
        | 'optionLabel'
        | 'positionNumberCanBeSetByUser'
        | 'canHaveChildren'
        | 'canHavePrices'
        | 'Clone'
    > = {
        UpdatedAt: 'Aktualisiert am',
        Description: 'Langtext',
        Title: 'Titel',
        ArticleNumber: 'Artikelnummer',
        PositionNumber: 'Positionsnummer',
        BVCustomNameId: 'Kostenart',
    }
    public get DisplayName(): string {
        return this.Description;
    }
    public get optionLabel(): string {
        return this.DisplayName;
    }

    public static readonly positionNumberCanBeSetByUser = ({PositionType}: {PositionType: LVPositionType}) => LVPositionTypeEditablePositionNumberByUser[PositionType];
    public get positionNumberCanBeSetByUser(): boolean {
        return AvaLvEntryEntity.positionNumberCanBeSetByUser(this);
    }

    public static readonly canHaveChildren = ({PositionType}: {PositionType: LVPositionType}) => LVPositionTypeCanHaveChildren[PositionType];
    public get canHaveChildren(): boolean {
        return AvaLvEntryEntity.canHaveChildren(this);
    }

    public static readonly canHavePrices = ({PositionType}: {PositionType: LVPositionType}) => LVPositionTypeCanHavePrices[PositionType];
    public get canHavePrices(): boolean {
        return AvaLvEntryEntity.canHavePrices(this);
    }

    public Clone(override: Partial<AvaLvEntryEntity> = {}): AvaLvEntryEntity {
        const attr = { ...this, ...override };
        return Object.assign(new AvaLvEntryEntity(), attr);
    }
    public static fromBackend(res: LVPosition): AvaLvEntryEntity {
        return new AvaLvEntryEntity(
            res.Id && +res.Id,
            null,
            res.ParentId ? +res.ParentId : null,
            res.OrderNumber ? +res.OrderNumber : null,
            res.ArticleNumber,
            res.PositionNumber,
            res.Quantity,
            res.QuantityId ? +res.QuantityId : null,
            res.EstimatedCostLabor ? +res.EstimatedCostLabor : null,
            res.EstimatedCostOther ? +res.EstimatedCostOther : null,
            res.PlannedCostLabor ? +res.PlannedCostLabor : null,
            res.PlannedCostOther ? +res.PlannedCostOther : null,
            res.PlannedCostSurchargesLabor ? +res.PlannedCostSurchargesLabor : null,
            res.PlannedCostSurchargesOther ? +res.PlannedCostSurchargesOther : null,
            res.PlannedSalesPriceLabor ? +res.PlannedSalesPriceLabor : null,
            res.PlannedSalesPriceOther ? +res.PlannedSalesPriceOther : null,
            res.ActualCost ? +res.ActualCost : null,
            res.TenderAssignmentId ? +res.TenderAssignmentId : null,
            res.PositionState,
            res.PositionType,
            res.Description,
            res.Title,
            res.UpdatedAt ? GetTimeFromTimestamp(+res.UpdatedAt) : null,
            res.BVCustomNameId ? +res.BVCustomNameId : null,
        );
    }
}
